// Wave 20.18 — Admin dashboard (superadmin only).
//
// Table of every user with role, platform access, last login,
// session count, and an Impersonate action. Impersonation stashes
// the caller's token under IMPERSONATOR_TOKEN_KEY and swaps in the
// short-lived token minted by /admin/users/:id/impersonate; a
// reload lands them into the target user's session. The banner
// (mounted globally by App.tsx) handles "return to your account".

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageLayout from './components/PageLayout';
import { colors } from './theme';
import { useCurrentUser } from './services/useCurrentUser';
import { IMPERSONATOR_TOKEN_KEY } from './components/ImpersonationBanner';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface AdminUserRow {
  id: number;
  email: string;
  username: string | null;
  displayName: string | null;
  role: string;
  platforms: string[];
  subscriptionTier: string;
  isActive: boolean;
  verifiedEmail: boolean;
  createdAt: string;
  lastLoggedIn: string | null;
  lastSeenAt: string | null;
  sessionCount: number;
  liabilityAcceptedVersion: string | null;
  liveSession: {
    activityType: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
    startedAt: string;
    lastHeartbeatAt: string;
    snapshot: Record<string, any>;
  } | null;
}

// Presence thresholds (ms). Any authenticated request bumps
// lastSeenAt on the server (throttled to once per minute), so
// "active" = a request within the last 3 minutes, "recent" = last
// hour, otherwise it's just a timestamp.
const ACTIVE_MS = 3 * 60 * 1000;
const RECENT_MS = 60 * 60 * 1000;

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function presenceOf(iso: string | null): 'active' | 'recent' | 'idle' | 'unknown' {
  if (!iso) return 'unknown';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff <= ACTIVE_MS) return 'active';
  if (diff <= RECENT_MS) return 'recent';
  return 'idle';
}

export default function Admin() {
  const currentUser = useCurrentUser();
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'forbidden' | 'error'>('loading');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [liveOnly, setLiveOnly] = useState(false);
  // Wave 20.25 — per-column filters. Text-typed filters do
  // case-insensitive substring; enum filters use 'all' as the
  // wildcard sentinel so a single 'if (f !== "all")' gate works.
  const [colFilters, setColFilters] = useState({
    id: '',
    status: 'all' as 'all' | 'active' | 'suspended' | 'unverified',
    live: 'all' as 'all' | 'live' | 'idle',
    email: '',
    username: '',
    displayName: '',
    role: 'all' as 'all' | 'general' | 'rapha' | 'superadmin',
    platform: 'all' as 'all' | 'commercial' | 'rapha',
    tier: 'all' as string,
    liability: 'all' as 'all' | 'accepted' | 'notAccepted',
  });
  const updateFilter = <K extends keyof typeof colFilters>(
    key: K,
    value: (typeof colFilters)[K],
  ) => setColFilters((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    // Trust the server, not the JWT-decoded role — a stale token
    // (issued before the DB role was elevated) still has role:
    // 'general' in its payload; we shouldn't short-circuit to
    // "Not authorized" locally. Attempt the fetch; the server
    // enforces via Auth + Superadmin guards.
    let cancelled = false;
    const load = async (initial: boolean) => {
      const jwt = localStorage.getItem(TOKEN_KEY);
      try {
        const r = await fetch(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (cancelled) return;
        if (r.status === 401 || r.status === 403) {
          if (initial) setStatus('forbidden');
          return;
        }
        if (!r.ok) throw new Error(`${r.status}`);
        setRows(await r.json());
        setStatus('ok');
      } catch (e: any) {
        if (cancelled) return;
        // Only flip to an error state on the first load — a blip
        // on a periodic refresh shouldn't blow away the visible
        // table.
        if (initial) {
          setError(e?.message || 'Failed to load');
          setStatus('error');
        }
      }
    };
    load(true);
    // Refresh every 30s so "Active now" chips reflect reality
    // without a manual page reload. The server bumps last_seen_at
    // on any authenticated request (throttled 60s), so 30s here
    // gives us two chances to catch a fresh bump.
    const timer = setInterval(() => load(false), 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const availableTiers = useMemo(() => {
    if (!rows) return [] as string[];
    return Array.from(new Set(rows.map((r) => r.subscriptionTier))).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    let out = rows;
    if (liveOnly) out = out.filter((r) => !!r.liveSession);
    if (needle) {
      out = out.filter((r) => {
        const bag = `${r.email} ${r.username ?? ''} ${r.displayName ?? ''} ${r.role} ${r.id}`;
        return bag.toLowerCase().includes(needle);
      });
    }
    // Wave 20.25 — apply per-column filters. Each is a no-op when
    // empty / 'all', so an untouched filter row leaves `out` alone.
    const cf = colFilters;
    if (cf.id) out = out.filter((r) => String(r.id).includes(cf.id.trim()));
    if (cf.email) out = out.filter((r) => r.email.toLowerCase().includes(cf.email.toLowerCase()));
    if (cf.username)
      out = out.filter((r) => (r.username ?? '').toLowerCase().includes(cf.username.toLowerCase()));
    if (cf.displayName)
      out = out.filter((r) =>
        (r.displayName ?? '').toLowerCase().includes(cf.displayName.toLowerCase()),
      );
    if (cf.role !== 'all') out = out.filter((r) => r.role === cf.role);
    if (cf.platform !== 'all') out = out.filter((r) => r.platforms.includes(cf.platform));
    if (cf.tier !== 'all') out = out.filter((r) => r.subscriptionTier === cf.tier);
    if (cf.live !== 'all')
      out = out.filter((r) => (cf.live === 'live' ? !!r.liveSession : !r.liveSession));
    if (cf.status !== 'all') {
      out = out.filter((r) => {
        if (cf.status === 'active') return r.isActive && r.verifiedEmail;
        if (cf.status === 'suspended') return !r.isActive && r.verifiedEmail;
        if (cf.status === 'unverified') return !r.verifiedEmail;
        return true;
      });
    }
    if (cf.liability !== 'all') {
      out = out.filter((r) =>
        cf.liability === 'accepted'
          ? !!r.liabilityAcceptedVersion
          : !r.liabilityAcceptedVersion,
      );
    }
    return out;
  }, [rows, q, liveOnly, colFilters]);

  const liveCount = useMemo(
    () => (rows ? rows.filter((r) => !!r.liveSession).length : 0),
    [rows],
  );

  // Wave 20.24 — Suspend / reactivate. POST to /admin/users/:id/(suspend|reactivate)
  // and mutate the row locally on success so the button + inactive
  // chip flip without a full refetch.
  const setActive = async (row: AdminUserRow, active: boolean) => {
    setBusyId(row.id);
    setError(null);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      const path = active ? 'reactivate' : 'suspend';
      const res = await fetch(`${API}/admin/users/${row.id}/${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `HTTP ${res.status}`);
      }
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === row.id ? { ...r, isActive: active } : r)) : prev,
      );
    } catch (e: any) {
      setError(e?.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const impersonate = async (row: AdminUserRow) => {
    setBusyId(row.id);
    setError(null);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/users/${row.id}/impersonate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { accessToken: string };
      // Stash the caller's own token so the banner can swap back.
      // If already impersonating (nested case), preserve the ORIGINAL
      // impersonator token — don't overwrite it with a target token.
      if (!localStorage.getItem(IMPERSONATOR_TOKEN_KEY)) {
        localStorage.setItem(IMPERSONATOR_TOKEN_KEY, jwt || '');
      }
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      window.location.href = '/feed';
    } catch (e: any) {
      setError(e?.message || 'Impersonation failed');
    } finally {
      setBusyId(null);
    }
  };

  if (status === 'forbidden') {
    // Two reasons this fires:
    //   - The caller is genuinely not a superadmin.
    //   - The caller IS a superadmin in the DB, but their browser
    //     JWT was minted before the role was elevated and still
    //     says role='general' internally. Server accepted the JWT
    //     (valid signature) but the Superadmin guard rejected on
    //     role. Cleanest fix: log out + back in to mint a fresh
    //     token with the current role claim.
    const staleTokenLikely =
      currentUser.role !== 'superadmin' && !!currentUser.id;
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Not authorized
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 1 }}>
            {staleTokenLikely
              ? 'If you were just made a superadmin, log out and log back in to refresh your session — your current token was issued before the role change.'
              : 'Only superadmins can view the admin dashboard.'}
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="xl">
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Superadmin
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Users
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 0.5 }}>
            All accounts — click Impersonate on a row to view aero.chat as that user.
            {rows && (
              <Box component="span" sx={{ ml: 1 }}>
                <b>{rows.length}</b> total.
              </Box>
            )}
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by email, username, display name, role, id…"
            sx={{ maxWidth: 460 }}
          />
          <Chip
            label={`In session · ${liveCount}`}
            clickable
            onClick={() => setLiveOnly((v) => !v)}
            sx={{
              fontWeight: 800,
              letterSpacing: '0.08em',
              backgroundColor: liveOnly ? colors.brand : 'transparent',
              color: liveOnly ? colors.textInverse : colors.brand,
              border: `1px solid ${colors.brand}88`,
            }}
          />
        </Stack>

        {filtered === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.brand }} />
          </Box>
        )}

        {filtered && (
          <TableContainer
            sx={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              overflowX: 'auto',
              // Wave 20.25 — sticky header needs a bounded height so
              // scroll is on the container, not the page.
              maxHeight: 'calc(100vh - 300px)',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={stickyHeadSx}>ID</TableCell>
                  <TableCell sx={stickyHeadSx}>Status</TableCell>
                  <TableCell sx={stickyHeadSx}>In session</TableCell>
                  <TableCell sx={stickyHeadSx}>Email</TableCell>
                  <TableCell sx={stickyHeadSx}>Username</TableCell>
                  <TableCell sx={stickyHeadSx}>Display name</TableCell>
                  <TableCell sx={stickyHeadSx}>Role</TableCell>
                  <TableCell sx={stickyHeadSx}>Platform</TableCell>
                  <TableCell sx={stickyHeadSx}>Tier</TableCell>
                  <TableCell sx={stickyHeadSx}>Sessions</TableCell>
                  <TableCell sx={stickyHeadSx}>Last seen</TableCell>
                  <TableCell sx={stickyHeadSx}>Last login</TableCell>
                  <TableCell sx={stickyHeadSx}>Created</TableCell>
                  <TableCell sx={stickyHeadSx}>Liability</TableCell>
                  <TableCell sx={stickyHeadSx} align="right">
                    Action
                  </TableCell>
                </TableRow>
                {/* Wave 20.25 — Filter row. Sticks to the top with the
                    header so filters stay reachable while scrolling. */}
                <TableRow>
                  <TableCell sx={stickyFilterSx}>
                    <FilterInput
                      value={colFilters.id}
                      onChange={(v) => updateFilter('id', v)}
                      placeholder="123"
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterSelect
                      value={colFilters.status}
                      onChange={(v) => updateFilter('status', v as any)}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'active', label: 'Active' },
                        { value: 'suspended', label: 'Suspended' },
                        { value: 'unverified', label: 'Unverified' },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterSelect
                      value={colFilters.live}
                      onChange={(v) => updateFilter('live', v as any)}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'live', label: 'Live now' },
                        { value: 'idle', label: 'Not live' },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterInput
                      value={colFilters.email}
                      onChange={(v) => updateFilter('email', v)}
                      placeholder="email"
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterInput
                      value={colFilters.username}
                      onChange={(v) => updateFilter('username', v)}
                      placeholder="handle"
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterInput
                      value={colFilters.displayName}
                      onChange={(v) => updateFilter('displayName', v)}
                      placeholder="name"
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterSelect
                      value={colFilters.role}
                      onChange={(v) => updateFilter('role', v as any)}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'general', label: 'general' },
                        { value: 'rapha', label: 'rapha' },
                        { value: 'superadmin', label: 'superadmin' },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterSelect
                      value={colFilters.platform}
                      onChange={(v) => updateFilter('platform', v as any)}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'commercial', label: 'commercial' },
                        { value: 'rapha', label: 'rapha' },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx}>
                    <FilterSelect
                      value={colFilters.tier}
                      onChange={(v) => updateFilter('tier', v)}
                      options={[
                        { value: 'all', label: 'All' },
                        ...availableTiers.map((t) => ({ value: t, label: t })),
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx} />
                  <TableCell sx={stickyFilterSx} />
                  <TableCell sx={stickyFilterSx} />
                  <TableCell sx={stickyFilterSx} />
                  <TableCell sx={stickyFilterSx}>
                    <FilterSelect
                      value={colFilters.liability}
                      onChange={(v) => updateFilter('liability', v as any)}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'accepted', label: 'Accepted' },
                        { value: 'notAccepted', label: 'Not accepted' },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={stickyFilterSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => {
                  const presence = presenceOf(row.lastSeenAt);
                  return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={cellSx}>{row.id}</TableCell>
                    <TableCell sx={cellSx}>
                      <PresenceChip presence={presence} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <LiveSessionCell live={row.liveSession} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {row.email}
                        {!row.verifiedEmail && (
                          <Chip label="unverified" size="small" sx={chipMutedSx} />
                        )}
                        {!row.isActive && (
                          <Chip label="inactive" size="small" sx={chipDangerSx} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {row.username ? `@${row.username}` : '—'}
                    </TableCell>
                    <TableCell sx={cellSx}>{row.displayName ?? '—'}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        size="small"
                        label={row.role}
                        sx={
                          row.role === 'superadmin'
                            ? chipBrandSx
                            : row.role === 'rapha'
                            ? chipDataSx
                            : chipMutedSx
                        }
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {row.platforms.map((p) => (
                          <Chip key={p} label={p} size="small" sx={chipMutedSx} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cellSx}>{row.subscriptionTier}</TableCell>
                    <TableCell sx={cellSx}>{row.sessionCount}</TableCell>
                    <TableCell sx={cellSx}>{fmtRelative(row.lastSeenAt)}</TableCell>
                    <TableCell sx={cellSx}>{fmtWhen(row.lastLoggedIn)}</TableCell>
                    <TableCell sx={cellSx}>{fmtWhen(row.createdAt)}</TableCell>
                    <TableCell sx={cellSx}>
                      {row.liabilityAcceptedVersion ?? (
                        <Chip label="not accepted" size="small" sx={chipDangerSx} />
                      )}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            busyId === row.id ||
                            row.id === currentUser.id ||
                            row.role === 'superadmin'
                          }
                          onClick={() => setActive(row, !row.isActive)}
                          sx={{
                            color: row.isActive ? colors.danger : colors.brand,
                            borderColor: row.isActive ? colors.danger : colors.brand,
                            fontWeight: 700,
                          }}
                        >
                          {busyId === row.id ? '…' : row.isActive ? 'Suspend' : 'Reactivate'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            busyId === row.id ||
                            row.id === currentUser.id ||
                            row.role === 'superadmin'
                          }
                          onClick={() => impersonate(row)}
                          sx={{
                            color: colors.brand,
                            borderColor: colors.brand,
                            fontWeight: 700,
                          }}
                        >
                          {busyId === row.id ? '…' : 'Impersonate'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ ...cellSx, color: colors.textMuted }}>
                      No users match that filter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </PageLayout>
  );
}

const headSx = {
  color: colors.textMuted,
  fontSize: '0.7rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  borderBottomColor: colors.border,
  fontWeight: 700,
};

// Wave 20.25 — sticky header + filter row on the Admin table.
// The Table's stickyHeader prop applies position: sticky to
// TableCells inside TableHead; we need the background painted
// explicitly here so scrolled-under body rows don't bleed through.
const stickyHeadSx = {
  ...headSx,
  backgroundColor: colors.surface,
  top: 0,
  zIndex: 3,
};
const stickyFilterSx = {
  backgroundColor: colors.surface,
  borderBottomColor: colors.border,
  padding: '4px 8px',
  top: 34,
  zIndex: 2,
  position: 'sticky' as const,
};

const cellSx = {
  color: colors.text,
  borderBottomColor: colors.border,
  fontSize: '0.85rem',
};

const chipBase = { fontWeight: 700, height: 20, fontSize: '0.65rem' };
const chipBrandSx = {
  ...chipBase,
  backgroundColor: colors.brand,
  color: colors.textInverse,
};
const chipDataSx = {
  ...chipBase,
  backgroundColor: `${colors.data}22`,
  color: colors.data,
};
const chipMutedSx = {
  ...chipBase,
  backgroundColor: colors.bg,
  color: colors.textMuted,
  border: `1px solid ${colors.border}`,
};
const chipDangerSx = {
  ...chipBase,
  backgroundColor: `${colors.danger}22`,
  color: colors.danger,
};

// ---------- column filters (Wave 20.25) ----------

function FilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={{
        width: '100%',
        '& .MuiInputBase-input': {
          fontSize: '0.75rem',
          padding: '4px 6px',
        },
      }}
    />
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="standard"
      disableUnderline
      sx={{
        width: '100%',
        fontSize: '0.75rem',
        '& .MuiSelect-select': { padding: '4px 6px' },
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
}

// ---------- presence ----------

function PresenceChip({
  presence,
}: {
  presence: 'active' | 'recent' | 'idle' | 'unknown';
}) {
  if (presence === 'active') {
    return (
      <Chip
        label="● Active now"
        size="small"
        sx={{
          ...chipBase,
          backgroundColor: `${colors.brand}22`,
          color: colors.brand,
          border: `1px solid ${colors.brand}55`,
        }}
      />
    );
  }
  if (presence === 'recent') {
    return (
      <Chip
        label="Recently active"
        size="small"
        sx={{
          ...chipBase,
          backgroundColor: `${colors.data}18`,
          color: colors.data,
          border: `1px solid ${colors.data}44`,
        }}
      />
    );
  }
  if (presence === 'idle') {
    return <Chip label="Offline" size="small" sx={chipMutedSx} />;
  }
  return <Chip label="—" size="small" sx={chipMutedSx} />;
}

function LiveSessionCell({
  live,
}: {
  live: AdminUserRow['liveSession'];
}) {
  if (!live) return <Box sx={{ color: colors.textFaint }}>—</Box>;
  const type =
    live.activityType === 'indoor_aero'
      ? 'Aero'
      : live.activityType === 'outdoor_cycling'
      ? 'Cycling'
      : 'Running';
  const elapsedSec = Math.max(
    0,
    Math.floor((Date.now() - new Date(live.startedAt).getTime()) / 1000),
  );
  const m = Math.floor(elapsedSec / 60);
  const s = elapsedSec % 60;
  const elapsed = `${m}:${String(s).padStart(2, '0')}`;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Chip
        label={`● ${type} · ${elapsed}`}
        size="small"
        sx={{
          ...chipBase,
          backgroundColor: colors.brand,
          color: colors.textInverse,
        }}
      />
      <Typography sx={{ color: colors.textFaint, fontSize: '0.65rem' }}>
        {fmtRelative(live.lastHeartbeatAt)}
      </Typography>
    </Box>
  );
}
